import React, { FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getCommunityProfile } from '../../modules/users/redux/operators';
import { LoadingModal } from '../../shared/components/loader';
import {
  ListComponent,
  ParagraphComponent,
} from '../../shared/components/text';
import ProfileIcon from '../../shared/components/topNav/components/ProfileIcon';
import { useAppDispatch, useAppSelector } from '../../shared/infra/redux';
import { BasicLayout } from '../../shared/layout';
import data from './privacy/privacyPageTemplate.json';

type PrivacyPolicyProps = {
  slug?: string;
};

enum ComponentTypes {
  PARAGRAPH = 'PARAGRAPH',
  LIST = 'LIST',
}

const BUILDER_COMPONENTS: Record<ComponentTypes, FC<any>> = {
  [ComponentTypes.PARAGRAPH]: ParagraphComponent,
  [ComponentTypes.LIST]: ListComponent,
};

type BuilderProps = {
  parent: ContainerProps;
  components: BuilderComponentProps[];
};

interface InjectedBuilderProps extends BuilderProps {
  key: number;
}

type ContainerProps = {
  containerProps: {
    [x: string]: string;
  };
};

type BuilderComponentProps = {
  type: ComponentTypes;
  componentProps: {
    [x: string]: string;
  };
  value: string;
};

interface InjectedBuilderComponentProps extends BuilderComponentProps {
  key: number;
}

const PrivacyPolicyPage = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const profile = useAppSelector((state) => state.public.profile);
  const { slug } = useParams<PrivacyPolicyProps>();

  const handleFetchProfile = useCallback(async () => {
    await dispatch(getCommunityProfile({ slug }));
    setIsLoading(false);
  }, [dispatch, slug]);

  const buildHandler: FC<InjectedBuilderProps> = ({
    parent,
    components,
    key,
  }) => {
    return (
      <div {...parent.containerProps} key={key}>
        {components.map((component, idx) =>
          componentHandler({ ...component, key: idx }),
        )}
      </div>
    );
  };

  const componentHandler: FC<InjectedBuilderComponentProps> = ({
    componentProps,
    type,
    value,
    key,
  }): JSX.Element => {
    const TheComponent = BUILDER_COMPONENTS[type];
    const injectedValue =
      value &&
      value.replaceAll('{{COMMUNITY}}', profile ? profile.name : 'Communikit');
    return <TheComponent {...componentProps} value={injectedValue} key={key} />;
  };

  const buildHeaderView = (): React.ReactNode | undefined => {
    if (!profile) return;

    return (
      <div className='flex flex-row items-center space-x-2'>
        <ProfileIcon imgSrc={profile.logo ? profile.logo : undefined} className="flex-shrink-0" />
        <h1 className='font-bold text-xs uppercase md:text-lg'>{profile.name}</h1>
      </div>
    );
  };

  useEffect(() => {
    if (slug) {
      setIsLoading(true);
      void handleFetchProfile();
    }
  }, [handleFetchProfile, slug]);

  return isLoading ? (
    <LoadingModal isLoading={true} />
  ) : (
    <BasicLayout withPrivacy={false} withLogo={profile && buildHeaderView()}>
      <div className='relative flex flex-col items-start justify-center flex-1 w-5/6 md:w-2/3 lg:w-1/3 mx-auto overflow-y-auto my-32 md:my-40'>
        {profile && profile?.logo && (
          <div className='w-full flex flex-1 items-center justify-center mb-6'>
            <img
              className='mx-auto max-w-full object-contain h-36 w-36 rounded-xl overflow-hidden'
              src={profile.logo}
              alt={`${profile.slug} logo small`}
            />
          </div>
        )}
        {data.map((item, idx) =>
          buildHandler({ ...(item as BuilderProps), key: idx }),
        )}
      </div>
    </BasicLayout>
  );
};

export default PrivacyPolicyPage;
